import { useState, useCallback } from 'react';
import { Job } from '../types/JobClosingSurvey';

interface UseJobClosingSurveyReturn {
  isVisible: boolean;
  currentJob: Job | null;
  showSurvey: (job: Job) => void;
  hideSurvey: () => void;
  handleSuccess: (message: string) => void;
  handleError: (error: string) => void;
}

/**
 * Custom hook for managing Job Closing Survey modal state
 */
export const useJobClosingSurvey = (): UseJobClosingSurveyReturn => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);

  const showSurvey = useCallback((job: Job) => {
    setCurrentJob(job);
    setIsVisible(true);
  }, []);

  const hideSurvey = useCallback(() => {
    setIsVisible(false);
    // Keep currentJob until modal animation completes
    setTimeout(() => {
      setCurrentJob(null);
    }, 300);
  }, []);

  const handleSuccess = useCallback((message: string) => {
    // Show success flash message
    const alertDiv = document.createElement('div');
    alertDiv.className = 'alert alert-success alert-dismissible fade show';
    alertDiv.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `;

    // Insert at the top of the main content
    const mainContent = document.querySelector('.container-fluid, .container, main, #main-content');
    if (mainContent && mainContent.firstChild) {
      mainContent.insertBefore(alertDiv, mainContent.firstChild);
    } else {
      document.body.insertBefore(alertDiv, document.body.firstChild);
    }

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      alertDiv?.remove();
    }, 5000);

    // Reload the page to refresh the job list
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }, []);

  const handleError = useCallback((error: string) => {
    // Show error flash message
    const alertDiv = document.createElement('div');
    alertDiv.className = 'alert alert-danger alert-dismissible fade show';
    alertDiv.innerHTML = `
      ${error}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `;

    // Insert at the top of the main content
    const mainContent = document.querySelector('.container-fluid, .container, main, #main-content');
    if (mainContent && mainContent.firstChild) {
      mainContent.insertBefore(alertDiv, mainContent.firstChild);
    } else {
      document.body.insertBefore(alertDiv, document.body.firstChild);
    }

    // Auto-dismiss after 8 seconds
    setTimeout(() => {
      alertDiv?.remove();
    }, 8000);
  }, []);

  return {
    isVisible,
    currentJob,
    showSurvey,
    hideSurvey,
    handleSuccess,
    handleError
  };
};